import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ticketsService from "../services/ticketsService";
import SimpleFileUpload from "../components/SimpleFileUpload";
import Loader from "../components/Loader";
import styles from "./TicketNoteCreate.module.css";

const TicketNoteCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const loadTicket = async () => {
      try {
        setLoading(true);
        setError(null);

        const ticketData = await ticketsService.getTicket(id);
        setTicket(ticketData);
        setLoading(false);
      } catch (err) {
        console.error("Error loading ticket:", err);
        setError(err.message || "Failed to load ticket");
        setLoading(false);
      }
    };

    loadTicket();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.description.trim()) {
      errors.description = "Content field is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const noteData = {
        ticket_id: parseInt(id),
        description: form.description.trim(),
      };

      await ticketsService.createTicketNote(noteData, uploadedFiles);

      // Redirect back to ticket show page
      navigate(`/tickets/${id}`);
    } catch (err) {
      console.error("Error creating note:", err);
      setError(err.message || "Failed to create note");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleFileRemove = (fileIndex) => {
    setUploadedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}

      <div className={styles.breadcrumbs}>
        <span
          className={styles.breadcrumbItem}
          onClick={() => navigate("/dashboard")}
        >
          Dashboard
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span
          className={styles.breadcrumbItem}
          onClick={() => navigate("/tickets")}
        >
          Tickets
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span
          className={styles.breadcrumbItem}
          onClick={() => navigate(`/tickets/${id}`)}
        >
          {ticket?.id || id}
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbActive}>Create Note</span>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Create ticket note</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  <div className={styles.formGroup}>
                    <label
                      htmlFor="description"
                      className={styles.labelRequired}
                    >
                      Description
                    </label>
                    <div className={styles.inputWrapper}>
                      <textarea
                        id="description"
                        name="description"
                        className={`${styles.formControl} ${
                          validationErrors.description ? styles.hasError : ""
                        }`}
                        rows="5"
                        style={{
                          height: '200px',
                          resize: 'vertical'
                        }}
                        value={form.description}
                        onChange={handleInputChange}
                        placeholder="Enter note description..."
                        required
                      />
                      {validationErrors.description && (
                        <div className={styles.errorMessage}>
                          {validationErrors.description}
                        </div>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* File Upload Section */}
                <fieldset className={styles.fileUploadSection}>
                  <div className={styles.formGroup}>
                    <label htmlFor="files" className={styles.fileLabel}>
                      Images (may choose multiple)
                    </label>
                    <div className={styles.fileInputWrapper}>
                      <SimpleFileUpload
                        onFilesSelected={handleFileUpload}
                        onFileRemove={handleFileRemove}
                        uploadedFiles={uploadedFiles}
                        multiple={true}
                        accept="image/*"
                        className={styles.fileInput}
                      />
                    </div>
                  </div>
                </fieldset>

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className={styles.spinner}></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className={styles.plusIcon}>+</i>
                        Add note
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>{error}</div>
        </div>
      )}
    </div>
  );
};

export default TicketNoteCreate;
